// pfs-row-actions.ts — Aktions-Zelle einer Listen-Zeile (PFS-594).
//
// Ersetzt den Button-Bau des je Cut-App-Bundle KOPIERTEN Helfers
// buildLifecycleRowActions (identity, practice — byte-identisch). Die
// eigentliche Duplikat-Masse (Button-DOM, Variant→.btn-Mapping, disabled/title,
// .row-actions-Layout + Styling) lebt jetzt hier; der Lifecycle-BRANCH samt
// deutscher Labels bleibt Consumer-Sache (App-Content, @pfs/components-Boundary).
//
// Generisch: el.actions = [{key,label,variant?,disabled?,title?,onClick?}].
//   variant ∈ primary|secondary|danger|success (Default secondary) → .btn.<v>.sm
//   token-basiert aus @pfs/design im eigenen Shadow (immun, auch am body nutzbar).
//
// API:
//   Property el.actions = RowAction[] — (neu)rendert die Buttons.
//   RowAction: { key, label, variant?, disabled?, title?, onClick? }.
//
// Events (bubbles + composed):
//   pfs:action  detail {key:string} — bei Klick auf eine (nicht-disabled) Aktion;
//               zusätzlich wird action.onClick() aufgerufen, falls gesetzt.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import { invokeCallback } from "../../utils/invoke-callback.js";
import { BUTTON_CSS } from "../../styles/button.js";

const STYLES = `
/* KEIN flex-wrap: die Aktions-Buttons bleiben in EINER Zeile (wie die alte
   .row-actions). In einer schmalen Actions-Spalte (width:1%) wuerde wrap sie
   sonst stapeln; zu enge Viewports faengt der data-table-overflow-x:auto ab. */
:host { display: inline-flex; gap: 0.4rem; }
${BUTTON_CSS}
`;

const VARIANTS = new Set(["primary", "secondary", "danger", "success"]);

export interface RowAction {
  key: string;
  label: string;
  variant?: string;
  disabled?: boolean;
  title?: string;
  onClick?: () => void | Promise<void>;
}

/** Typisierte Consumer-Sicht auf <pfs-row-actions>. */
export interface PfsRowActionsElement extends HTMLElement {
  actions: RowAction[];
}

defineComponent({
  tagName: "pfs-row-actions",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    let actions: RowAction[] = [];

    const self = host as PfsRowActionsElement;
    const preActions = takeOwnProperty<RowAction[]>(host, "actions");

    const renderActions = (): void => {
      for (const el of Array.from(root.querySelectorAll("button"))) el.remove();
      for (const a of actions) {
        const variant = a.variant && VARIANTS.has(a.variant) ? a.variant : "secondary";
        const b = document.createElement("button");
        b.type = "button";
        b.className = "btn " + variant + " sm";
        b.textContent = a.label;
        if (a.disabled) b.disabled = true;
        if (a.title) b.title = a.title;
        b.addEventListener("click", () => {
          if (a.disabled) return;
          host.dispatchEvent(
            new CustomEvent("pfs:action", { detail: { key: a.key }, bubbles: true, composed: true }),
          );
          invokeCallback(a.onClick); // sync + async (fire-and-forget) tolerant
        });
        root.appendChild(b);
      }
    };

    Object.defineProperty(host, "actions", {
      configurable: true,
      get: () => actions,
      set: (v: unknown) => {
        actions = Array.isArray(v) ? (v as RowAction[]) : [];
        renderActions();
      },
    });

    if (preActions !== undefined) self.actions = preActions;

    renderActions();
  },
});
